// crewbit-admin/src/pages/SetupGuide/items/SetupWizardItem.tsx
//
// Tier 2-UI Phase A §WorldClass.4 — SetupGuide item that mounts BootstrapWizard.
// Renders the wizard inline within the SetupGuide layout (back arrow + title
// header + body), mirroring TaxItem.tsx / HoursItem.tsx structure.

import { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { admin, palette, radii, spacing } from '../../../styles/tokens';
import BootstrapWizard from '../../../components/money/bootstrap/BootstrapWizard';
import type { ProvinceCode } from '../../../components/money/bootstrap/ProvincePicker';
import type { OnboardingAnswers } from '../../../types/business';

interface SetupWizardItemProps {
  businessId: string;
  onBack: () => void;
  onSaved: () => void;
}

interface BusinessRow {
  province: string | null;
  onboarding_answers: OnboardingAnswers | null;
}

export default function SetupWizardItem({ businessId, onBack, onSaved }: SetupWizardItemProps) {
  const [loading, setLoading] = useState(true);
  const [biz, setBiz] = useState<BusinessRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: bizErr } = await supabase
        .from('businesses')
        .select('province, onboarding_answers')
        .eq('id', businessId)
        .maybeSingle();
      if (cancelled) return;
      if (bizErr) {
        setError(`Couldn't load business: ${bizErr.message}`);
        setLoading(false);
        return;
      }
      setBiz((data ?? { province: null, onboarding_answers: null }) as BusinessRow);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  const subIndustryId =
    (biz?.onboarding_answers as { subIndustryId?: string | null } | null | undefined)
      ?.subIndustryId ?? null;
  const initialProvince = (biz?.province ?? null) as ProvinceCode | null;

  return (
    <div style={{ padding: spacing.xl, background: admin.bg, minHeight: '100vh' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.xs,
          background: 'transparent',
          border: 'none',
          color: palette.g600,
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
          padding: 0,
          marginBottom: spacing.lg,
          fontFamily: 'inherit',
        }}
      >
        <ChevronLeft size={16} />
        Back to setup guide
      </button>

      <div style={{ fontSize: 22, fontWeight: 700, color: palette.g800, marginBottom: spacing.xs }}>
        Quick tax setup
      </div>
      <div style={{ fontSize: 14, color: palette.g600, marginBottom: spacing.xl }}>
        Smart defaults for HST, mileage, payment terms — based on your province and trade.
        About 90 seconds.
      </div>

      {loading ? (
        <div style={{ color: palette.g500, fontSize: 13 }}>Loading…</div>
      ) : error ? (
        <div
          style={{
            background: palette.redWash,
            color: palette.red,
            borderRadius: radii.md,
            padding: spacing.md,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : (
        <BootstrapWizard
          businessId={businessId}
          initialProvince={initialProvince}
          subIndustryId={subIndustryId}
          onSuccess={onSaved}
          onSkip={onBack}
        />
      )}
    </div>
  );
}
